import './chrome.css';
import { formatDicomDate } from '../overlay';
import type { ViewerShell } from '../shell';

const UNKNOWN = 'Desconocido';
const EMPTY = '—';
const MS_PER_DAY = 86_400_000;
const MONTHS_PER_YEAR = 12;

function parseDicomDate(value: string): Date | undefined {
  if (!/^\d{8}$/.test(value)) return undefined;
  return new Date(Number(value.slice(0, 4)), Number(value.slice(4, 6)) - 1, Number(value.slice(6, 8)));
}

/** Age from birth date to study date (spec section 1): "N Años", else "N Meses", else "N Días"; "Desconocido" when unknown. */
export function patientAge(birthDate: string, onDate: string): string {
  const from = parseDicomDate(birthDate);
  if (!from) return UNKNOWN;
  const to = parseDicomDate(onDate) ?? new Date();
  if (to < from) return UNKNOWN;
  let months = (to.getFullYear() - from.getFullYear()) * MONTHS_PER_YEAR + (to.getMonth() - from.getMonth());
  if (to.getDate() < from.getDate()) months--;
  if (months >= MONTHS_PER_YEAR) return `${Math.floor(months / MONTHS_PER_YEAR)} Años`;
  if (months >= 1) return `${months} Meses`;
  return `${Math.max(0, Math.round((to.getTime() - from.getTime()) / MS_PER_DAY))} Días`;
}

const SEX_LABEL: Record<string, string> = { M: 'Masculino', F: 'Femenino', O: 'Otro' };

interface InfoField {
  key: string;
  label: string;
  value: string;
}

function field(key: string, label: string, value: string, fallback = EMPTY): InfoField {
  return { key, label, value: value || fallback };
}

function renderField({ key, label, value }: InfoField): HTMLElement {
  const item = document.createElement('span');
  item.className = 'info-field';
  item.dataset.field = key;
  const name = document.createElement('span');
  name.className = 'info-label';
  name.textContent = label;
  const text = document.createElement('span');
  text.className = 'info-value';
  text.textContent = value;
  item.title = `${label} ${value}`;
  item.append(name, text);
  return item;
}

/**
 * Patient strip under the top bar (`#toolbar-info`, spec section 1):
 * Sede <centerName> | Nombre paciente | ID paciente | Género | Fecha de nacimiento: | Edad:.
 */
export function install(shell: ViewerShell): void {
  const study = shell.study();
  const sex = study.patientSex.trim().toUpperCase();
  const fields = [
    field('center', 'Sede', shell.config.centerName),
    field('name', 'Nombre paciente', study.patientName),
    field('id', 'ID paciente', study.patientId),
    field('sex', 'Género', SEX_LABEL[sex] ?? sex, UNKNOWN),
    field('birthdate', 'Fecha de nacimiento:', formatDicomDate(study.patientBirthDate), UNKNOWN),
    field('age', 'Edad:', patientAge(study.patientBirthDate, study.studyDate)),
  ];
  shell.regions.info.replaceChildren(...fields.map(renderField));
  installReveal(shell.regions.info);
}

/** How long a tap keeps the full patient strip open (phones have no hover to read a cut-off name). */
export const REVEAL_MS = 5000;
const revealInstalled = new WeakSet<HTMLElement>();

/**
 * Tap (or Enter / Space) on the patient strip shows every field in full, wrapped onto more lines;
 * a second tap or REVEAL_MS later folds it back. The hover title stays for mouse users.
 */
function installReveal(strip: HTMLElement): void {
  if (revealInstalled.has(strip)) return;
  revealInstalled.add(strip);
  strip.tabIndex = 0;
  strip.setAttribute('role', 'button');
  strip.setAttribute('aria-expanded', 'false');
  strip.setAttribute('aria-label', 'Datos del paciente: tocar para ver completos');
  let timer: number | undefined;
  const set = (open: boolean) => {
    strip.classList.toggle('expanded', open);
    strip.setAttribute('aria-expanded', String(open));
    window.clearTimeout(timer);
    if (open) timer = window.setTimeout(() => set(false), REVEAL_MS);
  };
  strip.addEventListener('click', () => set(!strip.classList.contains('expanded')));
  strip.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    set(!strip.classList.contains('expanded'));
  });
}
